/**
 * Importación masiva de evaluaciones antropométricas de nutrición.
 *
 * Una fila = una evaluación de una clienta existente (identificada por
 * teléfono). Los valores derivados se calculan con las mismas fórmulas que
 * usa el diálogo manual y luego se envían al RPC del servidor.
 */

import type { Client } from '@/types';
import {
  ImportFieldDef,
  ParseResult,
  ParsedRow,
  RowIssue,
  cellText,
  inspectPhoneCell,
  normalizeHeader,
  parseDate,
  toNumber,
} from './import-utils';
import {
  ACTIVITY_LABELS,
  ActivityLevel,
  CompFormula,
  DensityEquation,
  DIAMETER_SITES,
  EnergyGoal,
  FORMULA_LABELS,
  PERIMETER_SITES,
  SKINFOLD_SITES,
  Sex,
} from './nutrition-formulas';
import { computeProgressResults, targetsFromEnergy } from './nutrition-progress-calc';
import { phonesMatch } from './phone-utils';

const BASE_FIELDS: ImportFieldDef[] = [
  {
    key: 'phone',
    label: 'Teléfono',
    aliases: ['telefono', 'phone', 'celular', 'whatsapp', 'movil', 'tel'],
    hint: 'Identifica a la clienta. Acepta +503 o solo los 8 dígitos.',
  },
  {
    key: 'recorded_at',
    label: 'Fecha de evaluación',
    required: true,
    aliases: ['fecha', 'fecha de evaluacion', 'fecha evaluacion', 'recorded at', 'date'],
    hint: 'dd/mm/aaaa o aaaa-mm-dd.',
  },
  {
    key: 'full_name',
    label: 'Nombre completo',
    aliases: ['nombre completo', 'nombre y apellido', 'nombres y apellidos', 'full name', 'clienta'],
    hint: 'Opcional; respaldo si falta el teléfono.',
  },
  { key: 'sex', label: 'Sexo', aliases: ['sexo', 'genero', 'sex'], hint: 'M/F, masculino/femenino.' },
  { key: 'age_years', label: 'Edad', aliases: ['edad', 'age', 'anos', 'años'] },
  { key: 'weight_kg', label: 'Peso (kg)', required: true, aliases: ['peso', 'weight', 'peso kg', 'kg'] },
  { key: 'height_cm', label: 'Talla (cm)', aliases: ['talla', 'estatura', 'altura', 'height', 'talla cm'] },
  {
    key: 'sitting_height_cm',
    label: 'Talla sentado (cm)',
    aliases: ['talla sentado', 'altura sentado', 'sitting height'],
  },
  { key: 'waist_cm', label: 'Cintura (cm)', aliases: ['cintura', 'waist', 'perimetro cintura'] },
  { key: 'hip_cm', label: 'Cadera (cm)', aliases: ['cadera', 'hip', 'perimetro cadera'] },
  { key: 'chest_cm', label: 'Pecho (cm)', aliases: ['pecho', 'torax', 'chest', 'perimetro torax'] },
  { key: 'arm_cm', label: 'Brazo (cm)', aliases: ['brazo', 'arm', 'perimetro brazo'] },
  { key: 'thigh_cm', label: 'Muslo (cm)', aliases: ['muslo', 'thigh', 'perimetro muslo'] },
  {
    key: 'muscle_kg',
    label: 'Músculo (kg)',
    aliases: ['musculo', 'musculo kg', 'masa muscular', 'muscle', 'skeletal muscle', 'muscle mass'],
    hint: 'Captura manual de la báscula; no es la masa muscular derivada.',
  },
  {
    key: 'body_fat_manual',
    label: 'Grasa corporal manual (%)',
    aliases: ['grasa corporal', 'grasa corporal %', 'porcentaje de grasa', '% grasa', 'body fat', 'pbf'],
    hint: 'Se usa si no hay pliegues suficientes.',
  },
  {
    key: 'visceral_fat_manual',
    label: 'Grasa visceral manual (kg)',
    aliases: ['grasa visceral', 'visceral fat', 'grasa visceral kg'],
    hint: 'Opcional; si viene, reemplaza la estimación.',
  },
  {
    key: 'adherence_score',
    label: 'Adherencia (%)',
    aliases: ['adherencia', 'adherence', 'adherencia al plan'],
  },
  {
    key: 'activity_level',
    label: 'Nivel de actividad',
    aliases: ['actividad', 'nivel de actividad', 'activity', 'activity level'],
    hint: 'sedentary, light, moderate, active o very_active.',
  },
  {
    key: 'formula',
    label: 'Fórmula',
    aliases: ['formula', 'metodo', 'método', 'method'],
    hint: 'jp3, jp7, durnin o yuhasz.',
  },
  {
    key: 'density_equation',
    label: 'Ecuación de densidad',
    aliases: ['ecuacion', 'ecuación', 'equation', 'siri', 'brozek'],
    hint: 'siri o brozek.',
  },
  {
    key: 'goal',
    label: 'Objetivo',
    aliases: ['objetivo', 'goal'],
    hint: 'deficit, maintenance o surplus.',
  },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes', 'observaciones', 'observacion'] },
];

const SKINFOLD_FIELDS: ImportFieldDef[] = SKINFOLD_SITES.map((site) => ({
  key: `skinfold_${site.key}`,
  label: `Pliegue: ${site.label}`,
  aliases: [`pliegue ${site.label}`, `pliegue cutaneo ${site.label}`, `skinfold ${site.label}`],
  hint: 'Milímetros (mm).',
}));

const PERIMETER_FIELDS: ImportFieldDef[] = PERIMETER_SITES.map((site) => ({
  key: `perimeter_${site.key}`,
  label: `Perímetro: ${site.label}`,
  aliases: [`perimetro ${site.label}`, `perímetro ${site.label}`, `circunferencia ${site.label}`],
  hint: 'Centímetros (cm).',
}));

const DIAMETER_FIELDS: ImportFieldDef[] = DIAMETER_SITES.map((site) => ({
  key: `diameter_${site.key}`,
  label: `Diámetro: ${site.label}`,
  aliases: [`diametro ${site.label}`, `diámetro ${site.label}`, `diametro oseo ${site.label}`],
  hint: 'Centímetros (cm).',
}));

export const NUTRITION_PROGRESS_FIELDS: ImportFieldDef[] = [
  ...BASE_FIELDS,
  ...SKINFOLD_FIELDS,
  ...PERIMETER_FIELDS,
  ...DIAMETER_FIELDS,
];

const normalizeKey = (value: unknown) => normalizeHeader(value);

const SEX_MAP: Record<string, Sex> = {
  m: 'male',
  male: 'male',
  masculino: 'male',
  hombre: 'male',
  f: 'female',
  female: 'female',
  femenino: 'female',
  mujer: 'female',
};

const ACTIVITY_MAP: Record<string, ActivityLevel> = {
  sedentario: 'sedentary',
  sedentaria: 'sedentary',
  sedentary: 'sedentary',
  ligero: 'light',
  ligera: 'light',
  light: 'light',
  moderado: 'moderate',
  moderada: 'moderate',
  moderate: 'moderate',
  activo: 'active',
  activa: 'active',
  active: 'active',
  muyactivo: 'very_active',
  muyactiva: 'very_active',
  veryactive: 'very_active',
};

const FORMULA_MAP: Record<string, CompFormula> = {
  jp3: 'jp3',
  '3pliegues': 'jp3',
  jacksonpollock3: 'jp3',
  jp7: 'jp7',
  '7pliegues': 'jp7',
  jacksonpollock7: 'jp7',
  durnin: 'durnin',
  durninwomersley: 'durnin',
  yuhasz: 'yuhasz',
};

const DENSITY_MAP: Record<string, DensityEquation> = {
  siri: 'siri',
  brozek: 'brozek',
};

const GOAL_MAP: Record<string, EnergyGoal> = {
  deficit: 'deficit',
  deficitcalorico: 'deficit',
  perdida: 'deficit',
  perdidadegrasa: 'deficit',
  maintenance: 'maintenance',
  mantenimiento: 'maintenance',
  surplus: 'surplus',
  superavit: 'surplus',
  ganancia: 'surplus',
};

const parseSex = (value: unknown): Sex | '' => SEX_MAP[normalizeKey(value)] || '';

const parseActivity = (value: unknown): ActivityLevel | null => {
  const key = normalizeKey(value);
  if (!key) return null;
  if (ACTIVITY_MAP[key]) return ACTIVITY_MAP[key];
  for (const [label, level] of Object.entries(ACTIVITY_LABELS)) {
    if (normalizeKey(label) === key) return level as ActivityLevel;
  }
  return null;
};

const parseFormula = (value: unknown): CompFormula | null => {
  const key = normalizeKey(value);
  if (!key) return null;
  if (FORMULA_MAP[key]) return FORMULA_MAP[key];
  for (const [formula, label] of Object.entries(FORMULA_LABELS)) {
    if (normalizeKey(label) === key) return formula as CompFormula;
  }
  return null;
};

const parseDensity = (value: unknown): DensityEquation | null => {
  const key = normalizeKey(value);
  if (!key) return null;
  return DENSITY_MAP[key] || null;
};

const parseGoal = (value: unknown): EnergyGoal | null => {
  const key = normalizeKey(value);
  if (!key) return null;
  return GOAL_MAP[key] || null;
};

export interface ClientResolution {
  client: Client | null;
  error?: string;
}

export interface NutritionParseOptions {
  resolveClient?: (phone: string, fullName: string) => ClientResolution;
}

export const parseNutritionProgressRows = (
  rawRows: Record<string, unknown>[],
  mapping: Record<string, string>,
  rawTypedRows?: Record<string, unknown>[],
  options: NutritionParseOptions = {}
): ParseResult => {
  const issues: RowIssue[] = [];
  const rows: ParsedRow[] = [];
  let validCount = 0;

  rawRows.forEach((raw, index) => {
    const values: Record<string, any> = {};
    let rowOk = true;
    let rowError = '';

    const fail = (message: string) => {
      rowOk = false;
      if (!rowError) rowError = message;
    };

    const getText = (key: string) => {
      const column = mapping[key];
      return column ? cellText(raw[column]) : '';
    };

    const readNumber = (key: string, label: string, min: number, max: number, required = false) => {
      const text = getText(key);
      if (!text) {
        if (required) fail(`Falta ${label.toLowerCase()}`);
        return null;
      }
      const parsed = toNumber(text);
      if (parsed === null) {
        fail(`${label} no es un número válido`);
        return null;
      }
      if (parsed < min || parsed > max) {
        fail(`${label} debe estar entre ${min} y ${max}`);
        return null;
      }
      return parsed;
    };

    const phoneColumn = mapping.phone;
    const phoneValue = phoneColumn ? raw[phoneColumn] : undefined;
    const phoneTyped = phoneColumn ? rawTypedRows?.[index]?.[phoneColumn] : undefined;
    const phoneCheck = inspectPhoneCell(phoneValue, phoneTyped);
    values.phone = phoneCheck.phone;
    if (!phoneCheck.phone) fail(phoneCheck.error || 'Falta el teléfono');

    const recordedText = getText('recorded_at');
    const recordedAt = parseDate(recordedText);
    if (!recordedText) fail('Falta la fecha de evaluación');
    else if (!recordedAt) fail(`La fecha "${recordedText}" no es válida`);
    values.recorded_at = recordedAt;

    const fullName = getText('full_name');
    const resolution = options.resolveClient?.(phoneCheck.phone, fullName);
    const client = resolution?.client || null;
    values.__client_id = client?.client_id || null;
    values.__client_name = client?.name || '';

    if (options.resolveClient) {
      if (!phoneCheck.phone && !fullName) {
        fail('Falta el teléfono o el nombre completo de la clienta');
      } else if (resolution?.error) {
        fail(resolution.error);
      } else if (!client) {
        fail(
          phoneCheck.phone
            ? 'No se encontró una clienta con ese teléfono'
            : 'No se encontró una clienta con ese nombre'
        );
      }
    }

    const fileSex = parseSex(getText('sex'));
    const clientSex = parseSex(client?.sex);
    const sex = fileSex || clientSex;
    if (getText('sex') && !fileSex) fail('Sexo no válido (usa M/F o masculino/femenino)');
    if (!sex) fail('Falta el sexo de la clienta (agrégalo en el archivo o en su ficha)');
    values.sex = sex;

    const fileAge = readNumber('age_years', 'La edad', 0, 120);
    const ageYears = fileAge ?? (client?.age != null ? Number(client.age) : null);
    values.age_years = ageYears;

    values.weight_kg = readNumber('weight_kg', 'El peso', 20, 400, true);
    values.height_cm = readNumber('height_cm', 'La talla', 50, 250);
    values.sitting_height_cm = readNumber('sitting_height_cm', 'La talla sentado', 40, 150);
    values.waist_cm = readNumber('waist_cm', 'La cintura', 10, 300);
    values.hip_cm = readNumber('hip_cm', 'La cadera', 10, 300);
    values.chest_cm = readNumber('chest_cm', 'El pecho', 10, 300);
    values.arm_cm = readNumber('arm_cm', 'El brazo', 10, 300);
    values.thigh_cm = readNumber('thigh_cm', 'El muslo', 10, 300);
    values.muscle_kg = readNumber('muscle_kg', 'El músculo', 0, 150);
    values.body_fat_manual = readNumber('body_fat_manual', 'La grasa corporal', 0, 70);
    values.visceral_fat_manual = readNumber('visceral_fat_manual', 'La grasa visceral', 0, 30);
    values.adherence_score = readNumber('adherence_score', 'La adherencia', 0, 100);

    const activityText = getText('activity_level');
    const activity = parseActivity(activityText);
    if (activityText && !activity) fail('Nivel de actividad no válido');
    values.activity_level = activity || 'moderate';

    const formulaText = getText('formula');
    const formula = parseFormula(formulaText);
    if (formulaText && !formula) fail('Fórmula no válida (jp3, jp7, durnin o yuhasz)');
    values.formula = formula || 'jp7';

    const densityText = getText('density_equation');
    const density = parseDensity(densityText);
    if (densityText && !density) fail('Ecuación no válida (siri o brozek)');
    values.density_equation = density || 'siri';

    const goalText = getText('goal');
    const goal = parseGoal(goalText);
    if (goalText && !goal) fail('Objetivo no válido (deficit, maintenance o surplus)');
    values.goal = goal || 'maintenance';

    values.notes = getText('notes') || null;

    const skinfolds: Record<string, number | null> = {};
    SKINFOLD_SITES.forEach((site) => {
      skinfolds[site.key] = readNumber(`skinfold_${site.key}`, `El pliegue ${site.label}`, 0, 100);
    });
    values.skinfolds = skinfolds;

    const perimeters: Record<string, number | null> = {};
    PERIMETER_SITES.forEach((site) => {
      perimeters[site.key] = readNumber(`perimeter_${site.key}`, `El perímetro ${site.label}`, 0, 300);
    });
    values.perimeters = perimeters;

    const diameters: Record<string, number | null> = {};
    DIAMETER_SITES.forEach((site) => {
      diameters[site.key] = readNumber(`diameter_${site.key}`, `El diámetro ${site.label}`, 0, 300);
    });
    values.diameters = diameters;

    if (rowOk) validCount += 1;
    else {
      issues.push({
        row: index + 1,
        message: rowError || 'Revisa los datos de la fila',
      });
    }

    rows.push({ index: index + 1, values });
  });

  return { rows, issues, validCount };
};

export const buildNutritionProgressPayload = (
  result: ParseResult
): Record<string, any>[] =>
  result.rows
    .filter((row) => !result.issues.some((issue) => issue.row === row.index))
    .map((row) => {
      const v = row.values;
      const results = computeProgressResults({
        sex: v.sex,
        ageYears: v.age_years,
        weightKg: v.weight_kg,
        heightCm: v.height_cm,
        waistCm: v.waist_cm,
        hipCm: v.hip_cm,
        formula: v.formula,
        densityEquation: v.density_equation,
        skinfolds: v.skinfolds,
        manualBodyFatPct: v.body_fat_manual,
        activityLevel: v.activity_level,
        goal: v.goal,
        manualVisceralKg: v.visceral_fat_manual,
      });
      const targets = targetsFromEnergy(results.energy);

      return {
        client_id: v.__client_id,
        recorded_at: v.recorded_at,
        sex: v.sex,
        age_years: v.age_years,
        weight_kg: v.weight_kg,
        height_cm: v.height_cm,
        sitting_height_cm: v.sitting_height_cm,
        waist_cm: v.waist_cm,
        hip_cm: v.hip_cm,
        chest_cm: v.chest_cm,
        arm_cm: v.arm_cm,
        thigh_cm: v.thigh_cm,
        activity_level: v.activity_level,
        formula: v.formula,
        skinfolds: v.skinfolds,
        perimeters: v.perimeters,
        diameters: v.diameters,
        body_fat_pct: results.bodyFat,
        fat_mass_kg: results.fatMassKg,
        lean_mass_kg: results.leanMassKg,
        body_density: results.composition.bodyDensity,
        icc: results.indices.icc,
        ict: results.indices.ict,
        bmr: results.energy.bmr,
        tdee: results.energy.tdee,
        target_calories: targets.target_calories,
        target_protein: targets.target_protein,
        target_carbs: targets.target_carbs,
        target_fat: targets.target_fat,
        muscle_kg: v.muscle_kg,
        metabolic_age: results.metabolicAge.metabolicAge,
        visceral_fat_kg: results.visceralFat.visceralFatKg,
        visceral_fat_index: results.visceralFat.visceralFatIndex,
        visceral_fat_source: results.visceralFat.source,
        muscle_mass_kg: results.muscleMass.muscleMassKg,
        adherence_score: v.adherence_score,
        notes: v.notes,
      };
    });

/** Resuelve una clienta por teléfono usando la misma clave nacional. */
export const findClientByPhone = (clients: Client[], phone: string): Client | null =>
  clients.find((client) => phonesMatch(client.phone, phone)) || null;

/** Normaliza un nombre para comparar sin acentos, mayúsculas ni signos. */
export const normalizePersonName = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Busca clientas por nombre completo exacto (puede devolver varias). */
export const findClientsByName = (clients: Client[], fullName: string): Client[] => {
  const key = normalizePersonName(fullName);
  if (!key) return [];
  return clients.filter((client) => normalizePersonName(client.name) === key);
};
